import { useEffect, useMemo, useRef, useState } from "react";
import { readSheet } from "read-excel-file/universal";
import type { PlatformClient } from "../../platform/supabase";
import "./AdminDataMigrationCenter.css";

type View="SCHOOLS"|"VALIDATE"|"IMPORTS"|"LIFECYCLE";

type ImportSummary={
  runId:string;
  sourceType:string;
  status:string;
  requestedCount:number;
  createdCount:number;
  updatedCount:number;
  reactivatedCount:number;
  skippedCount:number;
  failedCount:number;
  createdAt:string;
  completedAt?:string|null;
};

type SchoolRow={
  schoolId:string;
  schoolName:string;
  schoolCode?:string|null;
  academicYearId?:string|null;
  yearCode?:string|null;
  academicYearStatus?:string|null;
  dataState:string;
  activeStudents:number;
  classCount:number;
  studentsWithoutClass:number;
  studentsWithoutIdentifier:number;
  importRunCount:number;
  importFailedRows:number;
  outgoingTransferPending:number;
  incomingTransferPending:number;
  preparedTransitions:number;
  openRosterPlans:number;
  plannedAcademicYears:number;
  latestImport?:ImportSummary|null;
  signals:Record<string,number>;
};

type RecentImport=ImportSummary & {
  schoolId:string;
  schoolName:string;
  schoolCode?:string|null;
  yearCode:string;
};

type Transfer={
  transferId:string;
  transferKind:string;
  status:string;
  transferDate:string;
  sourceSchoolId:string;
  sourceSchoolName:string;
  destinationSchoolId?:string|null;
  destinationSchoolName?:string|null;
  createdAt:string;
  completedAt?:string|null;
};

type Transition={
  batchId:string;
  schoolId:string;
  schoolName:string;
  status:string;
  sourceYearCode:string;
  targetYearCode:string;
  itemCount:number;
  summary:Record<string,unknown>;
  createdAt:string;
  appliedAt?:string|null;
};

type Workspace={
  root:{organizationId:string;name:string;code?:string|null;type:string};
  summary:{
    schoolCount:number;
    readySchools:number;
    noActiveYearSchools:number;
    importRequiredSchools:number;
    importIssueSchools:number;
    classMappingSchools:number;
    identifierGapSchools:number;
    transferPendingSchools:number;
    transitionPendingSchools:number;
    activeStudents:number;
    failedImportRows:number;
  };
  schools:SchoolRow[];
  recentImports:RecentImport[];
  recentTransfers:Transfer[];
  recentTransitions:Transition[];
};

type ImportRow={
  studentKey:string;
  displayName:string;
  gradeLevel:string;
  classLabel:string;
  email:string;
};

type ValidationRow={
  rowNumber:number;
  status:"OK"|"WARNING"|"ERROR";
  plannedAction:"CREATE"|"UPDATE"|"REACTIVATE";
  classMapping:"EXISTING"|"CREATE"|"UNASSIGNED";
  issues:string[];
};

type ValidationResult={
  school:{schoolId:string;schoolName:string;academicYearId:string;yearCode:string};
  summary:{
    rowCount:number;
    validRows:number;
    errorRows:number;
    warningRows:number;
    createRows:number;
    updateRows:number;
    reactivateRows:number;
    classCreateRows:number;
    unassignedClassRows:number;
    duplicateKeyRows:number;
    missingEmailRows:number;
  };
  rows:ValidationRow[];
};

type ImportRunDetail={
  run:RecentImport;
  actionCounts:Record<string,number>;
  items:Array<{
    rowNumber:number;
    studentKeyMasked:string;
    action:string;
    emailConfigured?:boolean|null;
    emailMasked?:string|null;
    errorCode?:string|null;
    errorMessage?:string|null;
  }>;
};

const HEADER_ALIASES:Record<string,keyof ImportRow>={
  "학생코드":"studentKey","학번":"studentKey","studentkey":"studentKey","studentid":"studentKey","id":"studentKey",
  "학생이름":"displayName","이름":"displayName","성명":"displayName","displayname":"displayName","name":"displayName",
  "학년":"gradeLevel","grade":"gradeLevel","gradelevel":"gradeLevel",
  "반":"classLabel","학급":"classLabel","class":"classLabel","classlabel":"classLabel",
  "이메일":"email","메일":"email","email":"email"
};

const SIGNAL_LABEL:Record<string,string>={
  noActiveYear:"활성 학년도 없음",
  importIssue:"Import 오류행",
  importRequired:"학생 명단 필요",
  classMapping:"학급 미매핑",
  identifierGap:"학생코드 미연결",
  transferPending:"전학 처리 대기",
  transitionPending:"학년이동 준비"
};

function normalizeHeader(value:unknown){
  return String(value ?? "").replace(/[\s_-]+/g,"").toLowerCase();
}

function parseCsv(text:string){
  const rows:string[][]=[];
  let row:string[]=[];
  let cell="";
  let quoted=false;
  const input=text.replace(/^\uFEFF/,"");

  for(let i=0;i<input.length;i++){
    const ch=input[i];
    if(ch==='"'){
      if(quoted && input[i+1]==='"'){
        cell+='"';
        i++;
      }else{
        quoted=!quoted;
      }
    }else if(ch==="," && !quoted){
      row.push(cell);
      cell="";
    }else if((ch==="\n" || ch==="\r") && !quoted){
      if(ch==="\r" && input[i+1]==="\n")i++;
      row.push(cell);
      cell="";
      if(row.some((value)=>value.trim()!==""))rows.push(row);
      row=[];
    }else{
      cell+=ch;
    }
  }

  row.push(cell);
  if(row.some((value)=>value.trim()!==""))rows.push(row);
  return rows;
}

function matrixToRows(matrix:unknown[][]):ImportRow[]{
  if(matrix.length<2)return [];
  const headers=matrix[0].map((value)=>HEADER_ALIASES[normalizeHeader(value)] || null);
  const required=["studentKey","displayName","gradeLevel"] as Array<keyof ImportRow>;

  for(const field of required){
    if(!headers.includes(field)){
      throw new Error("필수 열이 없습니다: 학생코드/학번, 학생이름/이름, 학년");
    }
  }

  return matrix.slice(1).flatMap((cells)=>{
    const row:ImportRow={
      studentKey:"",
      displayName:"",
      gradeLevel:"",
      classLabel:"",
      email:""
    };

    headers.forEach((field,index)=>{
      if(field)row[field]=String(cells[index] ?? "").trim();
    });

    return Object.values(row).some((value)=>value!=="") ? [row] : [];
  });
}

function csvCell(value:unknown){
  const text=value==null?"":String(value);
  return '"'+text.replace(/"/g,'""')+'"';
}

function downloadCsv(rows:Array<Record<string,unknown>>,filename:string){
  if(!rows.length)return;
  const headers=Object.keys(rows[0]);
  const body=[
    headers.map(csvCell).join(","),
    ...rows.map((row)=>headers.map((key)=>csvCell(row[key])).join(","))
  ].join("\r\n");
  const blob=new Blob(["\uFEFF",body],{type:"text/csv;charset=utf-8"});
  const url=URL.createObjectURL(blob);
  const a=document.createElement("a");
  a.href=url;
  a.download=filename;
  a.click();
  URL.revokeObjectURL(url);
}

function fmt(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleString("ko-KR",{
    year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"
  });
}

function stateLabel(value:string){
  const labels:Record<string,string>={
    NO_ACTIVE_YEAR:"학년도 설정 필요",
    IMPORT_ISSUE:"Import 오류",
    IMPORT_REQUIRED:"학생 명단 필요",
    CLASS_MAPPING_REQUIRED:"학급 매핑 필요",
    IDENTIFIER_GAP:"학생코드 연결 필요",
    TRANSFER_PENDING:"전학 처리 대기",
    TRANSITION_PENDING:"학년이동 진행",
    READY:"데이터 정상"
  };
  return labels[value] || value;
}

function importStatus(value:string){
  const labels:Record<string,string>={
    RUNNING:"실행 중",SUCCEEDED:"완료",PARTIAL:"부분 완료",FAILED:"실패"
  };
  return labels[value] || value;
}

function actionLabel(value:string){
  const labels:Record<string,string>={
    CREATE:"신규",UPDATE:"갱신",REACTIVATE:"재등록",CREATED:"신규",UPDATED:"갱신",REACTIVATED:"재등록",FAILED:"실패"
  };
  return labels[value] || value;
}

export function AdminDataMigrationCenter({
  client,
  rootOrganizationId
}:{client:PlatformClient;rootOrganizationId:string}){
  const [view,setView]=useState<View>("SCHOOLS");
  const [data,setData]=useState<Workspace|null>(null);
  const [selectedSchoolId,setSelectedSchoolId]=useState("");
  const [schoolQuery,setSchoolQuery]=useState("");
  const [stateFilter,setStateFilter]=useState("ALL");
  const [validationSchoolId,setValidationSchoolId]=useState("");
  const [fileName,setFileName]=useState("");
  const [importRows,setImportRows]=useState<ImportRow[]>([]);
  const [validation,setValidation]=useState<ValidationResult|null>(null);
  const [selectedRunId,setSelectedRunId]=useState("");
  const [runDetail,setRunDetail]=useState<ImportRunDetail|null>(null);
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [message,setMessage]=useState("");
  const fileRef=useRef<HTMLInputElement|null>(null);

  useEffect(()=>{
    let active=true;
    setError("");
    client.rpc("admin_data_migration_workspace",{p_root_organization_id:rootOrganizationId}).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const next=data as Workspace;
      setData(next);
      setSelectedSchoolId((current)=>
        next.schools.some((item)=>item.schoolId===current)
          ? current
          : next.schools[0]?.schoolId || ""
      );
      setValidationSchoolId((current)=>
        next.schools.some((item)=>item.schoolId===current)
          ? current
          : next.schools[0]?.schoolId || ""
      );
      setSelectedRunId((current)=>
        next.recentImports.some((item)=>item.runId===current)
          ? current
          : next.recentImports[0]?.runId || ""
      );
    });
    return()=>{active=false;};
  },[client,rootOrganizationId]);

  useEffect(()=>{
    if(!selectedRunId){
      setRunDetail(null);
      return;
    }
    let active=true;
    client.rpc("admin_student_import_run_detail",{
      p_root_organization_id:rootOrganizationId,
      p_run_id:selectedRunId
    }).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      setRunDetail(data as ImportRunDetail);
    });
    return()=>{active=false;};
  },[client,rootOrganizationId,selectedRunId]);

  const selectedSchool=useMemo(
    ()=>data?.schools.find((item)=>item.schoolId===selectedSchoolId) || null,
    [data,selectedSchoolId]
  );

  const visibleSchools=useMemo(()=>{
    const keyword=schoolQuery.trim().toLowerCase();
    return (data?.schools || []).filter((item)=>{
      if(stateFilter!=="ALL" && item.dataState!==stateFilter)return false;
      return !keyword || [item.schoolName,item.schoolCode || "",item.yearCode || ""]
        .some((value)=>value.toLowerCase().includes(keyword));
    });
  },[data,schoolQuery,stateFilter]);

  useEffect(()=>{
    if(visibleSchools.length && !visibleSchools.some((item)=>item.schoolId===selectedSchoolId)){
      setSelectedSchoolId(visibleSchools[0].schoolId);
    }
  },[visibleSchools,selectedSchoolId]);

  async function chooseFile(file?:File){
    if(!file)return;
    setError("");
    setMessage("");
    setValidation(null);

    try{
      const lower=file.name.toLowerCase();
      let matrix:unknown[][];
      if(lower.endsWith(".xlsx")){
        matrix=await readSheet(file) as unknown[][];
      }else if(lower.endsWith(".csv")){
        matrix=parseCsv(await file.text());
      }else{
        throw new Error("CSV 또는 XLSX 파일만 사용할 수 있습니다.");
      }

      const rows=matrixToRows(matrix);
      if(!rows.length)throw new Error("검증할 학생 행이 없습니다.");
      if(rows.length>1000)throw new Error("한 번에 최대 1,000행까지 검증할 수 있습니다.");

      setFileName(file.name);
      setImportRows(rows);
      setMessage(`${rows.length}개 행을 읽었습니다. Dry-run 검증을 실행하세요.`);
    }catch(err){
      setFileName("");
      setImportRows([]);
      setError(err instanceof Error?err.message:"파일을 읽지 못했습니다.");
    }
  }

  async function validateRows(){
    if(!validationSchoolId || !importRows.length)return;
    setBusy("validate");
    setError("");
    setMessage("");
    const {data,error}=await client.rpc("admin_validate_student_import_rows",{
      p_root_organization_id:rootOrganizationId,
      p_school_id:validationSchoolId,
      p_rows:importRows
    });
    setBusy("");
    if(error){
      setError(error.message);
      return;
    }
    const result=data as ValidationResult;
    setValidation(result);
    setMessage(
      `Dry-run 완료 · 적용가능 ${result.summary.validRows} · 오류 ${result.summary.errorRows} · 경고 ${result.summary.warningRows}`
    );
  }

  function downloadTemplate(){
    downloadCsv([{
      studentKey:"2026-001",
      displayName:"홍길동",
      gradeLevel:"1",
      classLabel:"1",
      email:"student@example.com"
    }],"RS_CAREER_student_import_template.csv");
  }

  function downloadValidation(){
    if(!validation)return;
    downloadCsv(validation.rows.map((row,index)=>({
      rowNumber:row.rowNumber,
      studentKey:importRows[index]?.studentKey || "",
      displayName:importRows[index]?.displayName || "",
      gradeLevel:importRows[index]?.gradeLevel || "",
      classLabel:importRows[index]?.classLabel || "",
      email:importRows[index]?.email || "",
      validationStatus:row.status,
      plannedAction:row.plannedAction,
      classMapping:row.classMapping,
      issues:row.issues.join(" | ")
    })),`RS_CAREER_${validation.school.schoolName}_dryrun.csv`);
  }

  if(!data)return <p className="muted">데이터 이관 현황을 불러오는 중…</p>;

  const s=data.summary;

  return (
    <section className="data-migration-page">
      <header className="data-migration-hero">
        <div>
          <p className="eyebrow">DATA IMPORT & MIGRATION COMMAND CENTER</p>
          <h2>데이터 Import·이관 관제센터</h2>
          <p>
            학교별 학생명단 Import, 학급 매핑, 학생코드, 전학, 학년이동 상태를 통합 관제하고
            실제 적용 전 CSV/XLSX Dry-run 검증을 수행합니다.
          </p>
        </div>
        <div className={"data-migration-posture "+(s.importIssueSchools>0?"blocked":s.readySchools===s.schoolCount?"ready":"attention")}>
          <span>DATA READINESS</span>
          <strong>{s.importIssueSchools>0?"오류 확인":s.readySchools===s.schoolCount?"정상":"정비 필요"}</strong>
          <small>정상 {s.readySchools}/{s.schoolCount}개교</small>
        </div>
      </header>

      {error && <p className="error">{error}</p>}
      {message && <p className="teacher-success">{message}</p>}

      <div className="data-migration-kpis">
        <MigrationKpi label="학교" value={s.schoolCount} />
        <MigrationKpi label="활성 학생" value={s.activeStudents} />
        <MigrationKpi label="Import 필요" value={s.importRequiredSchools} tone={s.importRequiredSchools>0?"warn":"normal"} />
        <MigrationKpi label="Import 오류 학교" value={s.importIssueSchools} tone={s.importIssueSchools>0?"danger":"normal"} />
        <MigrationKpi label="학급 매핑 필요" value={s.classMappingSchools} tone={s.classMappingSchools>0?"warn":"normal"} />
        <MigrationKpi label="학생코드 누락 학교" value={s.identifierGapSchools} tone={s.identifierGapSchools>0?"warn":"normal"} />
        <MigrationKpi label="전학 처리 학교" value={s.transferPendingSchools} />
        <MigrationKpi label="학년이동 학교" value={s.transitionPendingSchools} />
      </div>

      <nav className="data-migration-tabs">
        <button className={view==="SCHOOLS"?"active":""} onClick={()=>setView("SCHOOLS")}>학교 데이터 상태</button>
        <button className={view==="VALIDATE"?"active":""} onClick={()=>setView("VALIDATE")}>CSV/XLSX Dry-run</button>
        <button className={view==="IMPORTS"?"active":""} onClick={()=>setView("IMPORTS")}>Import 이력</button>
        <button className={view==="LIFECYCLE"?"active":""} onClick={()=>setView("LIFECYCLE")}>전학·학년이동</button>
      </nav>

      {view==="SCHOOLS" && (
        <>
          <div className="migration-school-toolbar">
            <input value={schoolQuery} onChange={(event)=>setSchoolQuery(event.target.value)} placeholder="학교명·코드·학년도 검색" />
            <select value={stateFilter} onChange={(event)=>setStateFilter(event.target.value)}>
              <option value="ALL">전체 상태</option>
              <option value="NO_ACTIVE_YEAR">학년도 설정 필요</option>
              <option value="IMPORT_ISSUE">Import 오류</option>
              <option value="IMPORT_REQUIRED">학생 명단 필요</option>
              <option value="CLASS_MAPPING_REQUIRED">학급 매핑 필요</option>
              <option value="IDENTIFIER_GAP">학생코드 연결 필요</option>
              <option value="TRANSFER_PENDING">전학 처리 대기</option>
              <option value="TRANSITION_PENDING">학년이동 진행</option>
              <option value="READY">데이터 정상</option>
            </select>
          </div>

          <div className="migration-school-layout">
            <aside className="migration-school-list">
              <div className="panel-title"><strong>학교 데이터 Inbox</strong><span>{visibleSchools.length}개교</span></div>
              {visibleSchools.map((school)=>(
                <button key={school.schoolId} className={selectedSchoolId===school.schoolId?"active":""} onClick={()=>setSelectedSchoolId(school.schoolId)}>
                  <div>
                    <span className={"migration-state "+school.dataState.toLowerCase()}>{stateLabel(school.dataState)}</span>
                    <span>{school.yearCode || "학년도 없음"}</span>
                  </div>
                  <strong>{school.schoolName}</strong>
                  <p>학생 {school.activeStudents} · 학급 {school.classCount}</p>
                  <small>Import {school.importRunCount}회 · 실패행 누적 {school.importFailedRows}</small>
                </button>
              ))}
            </aside>

            <main className="migration-school-detail">
              {selectedSchool ? (
                <>
                  <header className="migration-school-detail-head">
                    <div>
                      <span className={"migration-state "+selectedSchool.dataState.toLowerCase()}>{stateLabel(selectedSchool.dataState)}</span>
                      <h2>{selectedSchool.schoolName}</h2>
                      <p>{selectedSchool.schoolCode || "학교코드 없음"} · {selectedSchool.yearCode || "활성 학년도 없음"}</p>
                    </div>
                    <button className="secondary" onClick={()=>{
                      setValidationSchoolId(selectedSchool.schoolId);
                      setView("VALIDATE");
                    }}>이 학교 파일 Dry-run</button>
                  </header>

                  <div className="migration-school-metrics">
                    <SchoolMetric label="활성 학생" value={selectedSchool.activeStudents} />
                    <SchoolMetric label="학급" value={selectedSchool.classCount} />
                    <SchoolMetric label="학급 미매핑" value={selectedSchool.studentsWithoutClass} attention={selectedSchool.studentsWithoutClass>0} />
                    <SchoolMetric label="학생코드 미연결" value={selectedSchool.studentsWithoutIdentifier} attention={selectedSchool.studentsWithoutIdentifier>0} />
                    <SchoolMetric label="전학 대기" value={selectedSchool.outgoingTransferPending+selectedSchool.incomingTransferPending} />
                    <SchoolMetric label="학년이동" value={selectedSchool.preparedTransitions+selectedSchool.openRosterPlans} />
                  </div>

                  <section className="migration-detail-card">
                    <div className="migration-section-head"><div><p className="eyebrow">DATA SIGNALS</p><h3>정비 신호</h3></div></div>
                    <div className="migration-signal-grid">
                      {Object.entries(selectedSchool.signals).map(([key,value])=>(
                        <div key={key}><span>{SIGNAL_LABEL[key] || key}</span><strong>{value}</strong></div>
                      ))}
                      {!Object.keys(selectedSchool.signals).length && (
                        <div className="migration-clear"><strong>데이터 상태 정상</strong><span>현재 정비 신호가 없습니다.</span></div>
                      )}
                    </div>
                  </section>

                  <section className="migration-detail-card">
                    <div className="migration-section-head">
                      <div><p className="eyebrow">LATEST IMPORT</p><h3>최근 학생명단 동기화</h3></div>
                      {selectedSchool.latestImport && (
                        <button className="text-button" onClick={()=>{
                          setSelectedRunId(selectedSchool.latestImport!.runId);
                          setView("IMPORTS");
                        }}>실행 상세 →</button>
                      )}
                    </div>

                    {selectedSchool.latestImport ? (
                      <div className="migration-import-summary">
                        <div><span>상태</span><strong>{importStatus(selectedSchool.latestImport.status)}</strong></div>
                        <div><span>요청</span><strong>{selectedSchool.latestImport.requestedCount}</strong></div>
                        <div><span>신규</span><strong>{selectedSchool.latestImport.createdCount}</strong></div>
                        <div><span>갱신</span><strong>{selectedSchool.latestImport.updatedCount}</strong></div>
                        <div><span>재등록</span><strong>{selectedSchool.latestImport.reactivatedCount}</strong></div>
                        <div className={selectedSchool.latestImport.failedCount>0?"attention":""}><span>실패</span><strong>{selectedSchool.latestImport.failedCount}</strong></div>
                      </div>
                    ):(
                      <p className="case-empty">학생명단 Import 이력이 없습니다.</p>
                    )}
                  </section>
                </>
              ):<p className="case-empty">학교를 선택하세요.</p>}
            </main>
          </div>
        </>
      )}

      {view==="VALIDATE" && (
        <section className="migration-validation-card">
          <div className="migration-section-head">
            <div>
              <p className="eyebrow">READ-ONLY PREVALIDATION</p>
              <h3>학생명단 CSV/XLSX Dry-run</h3>
              <p>이 검증은 학생 Master를 생성·수정하지 않습니다. 실제 적용은 학교 Center의 SCHOOL_ADMIN이 수행합니다.</p>
            </div>
            <div className="migration-validation-actions">
              <button className="secondary" onClick={downloadTemplate}>CSV 양식</button>
              <button className="secondary" onClick={()=>fileRef.current?.click()}>파일 선택</button>
              <input ref={fileRef} type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden onChange={(event)=>{
                const file=event.target.files?.[0];
                if(file)void chooseFile(file);
                event.currentTarget.value="";
              }} />
            </div>
          </div>

          <div className="migration-validation-config">
            <label>검증 학교
              <select value={validationSchoolId} onChange={(event)=>{
                setValidationSchoolId(event.target.value);
                setValidation(null);
              }}>
                {data.schools.map((school)=>(
                  <option key={school.schoolId} value={school.schoolId}>{school.schoolName} · {school.yearCode || "학년도 없음"}</option>
                ))}
              </select>
            </label>
            <div>
              <span>파일</span>
              <strong>{fileName || "선택 안 됨"}</strong>
              <small>{importRows.length}행</small>
            </div>
            <button className="primary" disabled={busy!=="" || !validationSchoolId || !importRows.length} onClick={validateRows}>
              {busy==="validate"?"검증 중…":"Dry-run 검증"}
            </button>
          </div>

          {importRows.length>0 && !validation && (
            <div className="migration-preview-table">
              <div className="migration-preview-row head"><span>#</span><span>학생코드</span><span>이름</span><span>학년</span><span>반</span><span>이메일</span></div>
              {importRows.slice(0,20).map((row,index)=>(
                <div className="migration-preview-row" key={index}>
                  <span>{index+1}</span><span>{row.studentKey}</span><strong>{row.displayName}</strong><span>{row.gradeLevel}</span><span>{row.classLabel || "-"}</span><span>{row.email || "-"}</span>
                </div>
              ))}
              {importRows.length>20 && <p className="muted">미리보기는 20행까지만 표시합니다.</p>}
            </div>
          )}

          {validation && (
            <>
              <div className="validation-kpis">
                <ValidationKpi label="전체" value={validation.summary.rowCount} />
                <ValidationKpi label="적용가능" value={validation.summary.validRows} tone="good" />
                <ValidationKpi label="오류" value={validation.summary.errorRows} tone={validation.summary.errorRows>0?"danger":"normal"} />
                <ValidationKpi label="경고" value={validation.summary.warningRows} tone={validation.summary.warningRows>0?"warn":"normal"} />
                <ValidationKpi label="신규" value={validation.summary.createRows} />
                <ValidationKpi label="갱신" value={validation.summary.updateRows} />
                <ValidationKpi label="재등록" value={validation.summary.reactivateRows} />
                <ValidationKpi label="신규 학급" value={validation.summary.classCreateRows} />
              </div>

              <div className="validation-result-head">
                <div>
                  <strong>{validation.school.schoolName}</strong>
                  <span>{validation.school.yearCode}학년도</span>
                </div>
                <button className="secondary" onClick={downloadValidation}>검증결과 CSV</button>
              </div>

              <div className="validation-result-table">
                <div className="validation-result-row head"><span>#</span><span>학생</span><span>상태</span><span>예상처리</span><span>학급</span><span>검증메시지</span></div>
                {validation.rows.map((result,index)=>{
                  const source=importRows[index];
                  return (
                    <div className="validation-result-row" key={result.rowNumber}>
                      <span>{result.rowNumber}</span>
                      <span><strong>{source?.displayName || "-"}</strong><small>{source?.studentKey || "-"}</small></span>
                      <span className={"validation-state "+result.status.toLowerCase()}>{result.status}</span>
                      <span>{actionLabel(result.plannedAction)}</span>
                      <span>{result.classMapping==="EXISTING"?"기존":result.classMapping==="CREATE"?"자동 생성":"미배정"}</span>
                      <span>{result.issues.length?result.issues.join(" · "):"문제 없음"}</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </section>
      )}

      {view==="IMPORTS" && (
        <div className="migration-history-layout">
          <aside className="migration-import-list">
            <div className="panel-title"><strong>최근 Import</strong><span>{data.recentImports.length}건</span></div>
            {data.recentImports.map((run)=>(
              <button key={run.runId} className={selectedRunId===run.runId?"active":""} onClick={()=>setSelectedRunId(run.runId)}>
                <div><strong>{run.schoolName}</strong><span className={"import-status "+run.status.toLowerCase()}>{importStatus(run.status)}</span></div>
                <p>{run.yearCode} · {run.sourceType}</p>
                <small>{fmt(run.createdAt)} · 요청 {run.requestedCount} / 실패 {run.failedCount}</small>
              </button>
            ))}
            {!data.recentImports.length && <p className="case-empty">Import 이력이 없습니다.</p>}
          </aside>

          <main className="migration-import-detail">
            {runDetail ? (
              <>
                <header>
                  <div><p className="eyebrow">IMPORT RUN</p><h2>{runDetail.run.schoolName}</h2><p>{runDetail.run.yearCode} · {runDetail.run.sourceType} · {fmt(runDetail.run.createdAt)}</p></div>
                  <span className={"import-status "+runDetail.run.status.toLowerCase()}>{importStatus(runDetail.run.status)}</span>
                </header>

                <div className="migration-import-summary">
                  <div><span>요청</span><strong>{runDetail.run.requestedCount}</strong></div>
                  <div><span>신규</span><strong>{runDetail.run.createdCount}</strong></div>
                  <div><span>갱신</span><strong>{runDetail.run.updatedCount}</strong></div>
                  <div><span>재등록</span><strong>{runDetail.run.reactivatedCount}</strong></div>
                  <div><span>건너뜀</span><strong>{runDetail.run.skippedCount}</strong></div>
                  <div className={runDetail.run.failedCount>0?"attention":""}><span>실패</span><strong>{runDetail.run.failedCount}</strong></div>
                </div>

                <section className="migration-detail-card">
                  <div className="migration-section-head"><div><p className="eyebrow">ROW RESULT</p><h3>행별 결과</h3></div><span>학생 이름 원문 미노출</span></div>
                  <div className="import-item-table">
                    <div className="import-item-row head"><span>#</span><span>학생코드</span><span>처리</span><span>이메일</span><span>오류</span></div>
                    {runDetail.items.map((item)=>(
                      <div className="import-item-row" key={item.rowNumber}>
                        <span>{item.rowNumber}</span>
                        <span>{item.studentKeyMasked}</span>
                        <span>{actionLabel(item.action)}</span>
                        <span>{item.emailMasked || (item.emailConfigured?"설정":"없음")}</span>
                        <span className={item.errorMessage?"attention":""}>{item.errorMessage || "정상"}</span>
                      </div>
                    ))}
                  </div>
                </section>
              </>
            ):<p className="case-empty">Import 실행을 선택하세요.</p>}
          </main>
        </div>
      )}

      {view==="LIFECYCLE" && (
        <div className="migration-lifecycle-grid">
          <section className="migration-detail-card">
            <div className="migration-section-head"><div><p className="eyebrow">TRANSFER</p><h3>최근 전입·전출</h3></div><span>{data.recentTransfers.length}건</span></div>
            <div className="migration-lifecycle-list">
              {data.recentTransfers.map((item)=>(
                <article key={item.transferId}>
                  <span className="lifecycle-status">{item.status}</span>
                  <div>
                    <strong>{item.sourceSchoolName} → {item.destinationSchoolName || "외부학교"}</strong>
                    <small>{item.transferKind} · {item.transferDate} · 생성 {fmt(item.createdAt)}</small>
                  </div>
                </article>
              ))}
              {!data.recentTransfers.length && <p className="case-empty">전학 이력이 없습니다.</p>}
            </div>
          </section>

          <section className="migration-detail-card">
            <div className="migration-section-head"><div><p className="eyebrow">ACADEMIC TRANSITION</p><h3>최근 학년이동</h3></div><span>{data.recentTransitions.length}건</span></div>
            <div className="migration-lifecycle-list">
              {data.recentTransitions.map((item)=>(
                <article key={item.batchId}>
                  <span className="lifecycle-status">{item.status}</span>
                  <div>
                    <strong>{item.schoolName}</strong>
                    <small>{item.sourceYearCode} → {item.targetYearCode} · 학생 {item.itemCount}명 · {fmt(item.createdAt)}</small>
                  </div>
                </article>
              ))}
              {!data.recentTransitions.length && <p className="case-empty">학년이동 이력이 없습니다.</p>}
            </div>
          </section>
        </div>
      )}
    </section>
  );
}

function MigrationKpi({label,value,tone="normal"}:{label:string;value:number;tone?:"normal"|"warn"|"danger"}){
  return <div className={"migration-kpi "+tone}><span>{label}</span><strong>{value.toLocaleString("ko-KR")}</strong></div>;
}

function SchoolMetric({label,value,attention=false}:{label:string;value:number;attention?:boolean}){
  return <div className={attention?"migration-school-metric attention":"migration-school-metric"}><span>{label}</span><strong>{value.toLocaleString("ko-KR")}</strong></div>;
}

function ValidationKpi({label,value,tone="normal"}:{label:string;value:number;tone?:"normal"|"warn"|"danger"|"good"}){
  return <div className={"validation-kpi "+tone}><span>{label}</span><strong>{value.toLocaleString("ko-KR")}</strong></div>;
}
